import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { BRAND } from "@/data/content";

const links = [
  { id: "about", label: "About" },
  { id: "services", label: "Coaching" },
  { id: "podcast", label: "Podcast" },
  { id: "testimonials", label: "Stories" },
  { id: "contact", label: "Connect" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-black/70 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-12 flex items-center justify-between h-20">
        <button
          data-testid="navbar-brand"
          onClick={() => go("top")}
          className="flex items-center gap-3 group"
        >
          <img
            src={BRAND.logo}
            alt="Tapping Into Life LLC"
            className="h-12 w-12 rounded-full object-cover ring-1 ring-white/10 group-hover:ring-brand-amber/60 transition"
          />
          <div className="hidden sm:flex flex-col leading-none text-left">
            <span className="font-serif text-lg text-white tracking-wide">
              Tapping Into Life
            </span>
            <span className="font-accent text-sm text-brand-gold -mt-0.5">
              Discovering all of you
            </span>
          </div>
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="text-sm tracking-wide text-zinc-400 hover:text-white transition relative group"
            >
              {l.label}
              <span className="absolute -bottom-2 left-0 h-px w-0 bg-brand-amber group-hover:w-full transition-all duration-300" />
            </button>
          ))}
          <button
            data-testid="navbar-cta-book"
            onClick={() => go("contact")}
            className="ml-2 px-5 py-2.5 rounded-full bg-brand-amber text-black text-sm font-semibold hover:bg-brand-amberHover transition"
          >
            Book a Call
          </button>
        </nav>

        <button
          data-testid="navbar-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden p-2 text-white"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
        </button>
      </div>

      {open && (
        <div data-testid="navbar-mobile-menu" className="md:hidden bg-black/95 border-t border-white/5">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className="text-left font-serif text-2xl text-white/90 hover:text-brand-amber transition"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="mobile-nav-cta"
              onClick={() => go("contact")}
              className="mt-2 px-5 py-3 rounded-full bg-brand-amber text-black font-semibold"
            >
              Book a Call
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
